import React, { useCallback, useContext, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { SaveToDocumentConnectorModal } from '../../Header/SaveToDocumentConnectorModal'
import { useMeetingLinkedFolder } from '../../Header/useMeetingLinkedFolder'
import { MeetingSummaryAgentContentRefContext } from '../MeetingSummaryAgentContentRefContext'

import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { selectMeetingSoleContact, selectRoomSession } from '../../../../../../store/meetingSummary/roomSession'
import { selectSelectedMeetingTemplate } from '../../../../../../store/meetingSummary/meetingTemplates'
import { selectMeetingSummaryAgentAiAgentResultId, setMeetingSummary } from '../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { aiAgentResultAnchor } from '../../../../../../store/documentConnectors/documentConnectors'
import { useLazyGetAiAgentResultQuery } from '../../../../../../store/ai/aiAgentResult'
import { useGetExternalUserByIdQuery } from '../../../../../../store/externalUser/externalUser'

import { DocumentExportType } from '../../../../../../components/DocumentConnector/models'
import { useExportToPDF } from '../../../../../../components/PDFExportButton/hooks'

import { type IntegrationServiceId } from '../../../../../../common/integrations'
import { formatDateYYYYMMDD } from '../../../../../../common/dateUtils'

import { AIAgentType } from '../../../../../../models/AIAgent'
import { type User } from '../../../../../../models/User'

interface MeetingSummaryDocumentConnectorExportModalProps {
  serviceId: IntegrationServiceId
  onHide: () => void
}

export const MeetingSummaryDocumentConnectorExportModal: React.FC<MeetingSummaryDocumentConnectorExportModalProps> = ({ serviceId, onHide }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const aiAgentResultId = useAppSelector(selectMeetingSummaryAgentAiAgentResultId)
  const soleContact = useAppSelector(selectMeetingSoleContact)
  const { data: soleContactData } = useGetExternalUserByIdQuery(accountId && soleContact ? { accountId, userId: soleContact.id } : skipToken, {
    refetchOnMountOrArgChange: true,
  })
  const [updatedContact, setUpdatedContact] = useState<User | null>(null)
  const contact = updatedContact ?? soleContactData?.data ?? null

  const meetingSummaryAgentContentRef = useContext(MeetingSummaryAgentContentRefContext)
  const { generatePDFBlob } = useExportToPDF(meetingSummaryAgentContentRef)

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  const { folderIds: initialFolderIds, isLoading: linkedFoldersLoading } = useMeetingLinkedFolder(serviceId, roomSession?.roomId, roomSession?.id)

  const handleSaved = useCallback(async () => {
    if (!roomSession || !selectedMeetingTemplate?.id) {
      return
    }

    const result = await triggerGetAiAgentResult(
      {
        accountId,
        type: AIAgentType.MEETING_SUMMARY,
        resultKey: `${roomSession.roomId}|${roomSession.id}|${selectedMeetingTemplate?.id}`,
      },
      false,
    ).unwrap()

    if (result) {
      dispatch(setMeetingSummary(result))
    }
  }, [accountId, roomSession, selectedMeetingTemplate, triggerGetAiAgentResult, dispatch])

  if (!roomSession) {
    return null
  }

  const anchor = aiAgentResultId ? aiAgentResultAnchor(aiAgentResultId) : null

  const dateStr = roomSession.startedAt ? formatDateYYYYMMDD(roomSession.startedAt) : ''
  const defaultFileName = `Meeting Summary${dateStr ? `_${dateStr}` : ''}.pdf`

  return (
    <SaveToDocumentConnectorModal
      serviceId={serviceId}
      show={true}
      onHide={onHide}
      generatePDFBlob={generatePDFBlob}
      defaultFileName={defaultFileName}
      initialFolderIds={initialFolderIds}
      initialFoldersReady={!linkedFoldersLoading}
      anchor={anchor}
      saveSyncStatus={anchor != null}
      onSaved={handleSaved}
      contact={contact}
      onContactUpdated={setUpdatedContact}
      exportType={DocumentExportType.MEETING_SUMMARY}
      roomSession={roomSession}
    />
  )
}
